/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { IDetailsList } from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { ScrollToMode } from '@fluentui/react/lib/List';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisText } from 'src/core/components/LuisText';
import { TablePagination } from 'src/core/components/TablePagination';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { UnSubmittedChangesPrompt } from 'src/core/components/UnsubmittedChangesPrompt';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { logButtonClick, logButtonClickWithCustomData, logToggleChange } from 'src/core/services/telemetry/eventsNamesTemplate';
import { noopUndefined } from 'src/core/utils/base';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { QnaMakerFiltersBlade } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerFiltersBlade';
import { QnaMakerQnaCommandBar } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaCommandBar';
import { QnaMakerQnaTable } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaTable';
import { QnaMakerFilterPillContainer } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFilterPillsContainer';
import { QnaMakerFiltersType, QuestionsBeingEditedFilterItem } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFiltersStore';
import {
    applyMetadataFilter,
    applyQuestionFilter,
    applySourcesFilter,
    getFlattenMetadata
} from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { DeletionOperations, QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerTestPane } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerTestPane';
import { useIdGeneratorForNewQnaPair } from 'src/qnaMaker/core/hooks/useIdGeneratorForNewQnaPair';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { qnaMakerQnaStoreToQnaSwagger } from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';
import { useQnaPaginatedAssets } from 'src/qnaMaker/modules/hooks/useQnaPaginatedAssets';
import { useQnaPairsActions } from 'src/qnaMaker/modules/hooks/useQnaPairsActions';

const messages = defineMessages({
    emptyStateMessage: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.emptyStateMessage',
        defaultMessage: "You haven't created any question and answer pairs yet."
    },
    deleteQnaPairModalTitle: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.deleteQnaPairModalTitle',
        defaultMessage: 'Delete the selected question answer pair(s)'
    },
    deleteQnaPairModalBody: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.deleteQnaPairModalBody',
        defaultMessage: 'Are you sure you want to delete the selected {selectedCount} pair(s)?'
    },
    deleteQnaMetadataModalTitle: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.deleteQnaMetadataModalTitle',
        defaultMessage: 'Delete metadata of the selected question answer pair(s)'
    },
    deleteQnaMetadataModalBody: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.deleteQnaMetadataModalBody',
        defaultMessage: 'Are you sure you want to delete the metadata of the selected pair(s)?'
    },
    deleteQnaPromptsModalTitle: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.deleteQnaPromptsModalTitle',
        defaultMessage: 'Delete follow-up prompts of the selected question answer pair(s)'
    },
    deleteQnaPromptsModalBody: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.deleteQnaPromptsModalBody',
        defaultMessage: 'Are you sure you want to delete the follow-up prompts of the selected pair(s)?'
    },
    noItemsFound: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.noItemsFound',
        defaultMessage: 'No items found.'
    },
    filtersApplied: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.filtersApplied',
        defaultMessage: 'Filtered by:'
    },
    clearAll: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.clearAll',
        defaultMessage: 'Clear all'
    },
    clearAllAriaLabel: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.clearAllAriaLabel',
        defaultMessage: 'Clear all filters'
    },
    importModalTitle: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.importModalTitle',
        defaultMessage: 'Import Question Answer Pairs'
    },
    unsavedChangesWarningMsg: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.unsavedChangesWarningMsg',
        defaultMessage: 'You have unsaved changes. Click on Save changes in the command bar to save your recent work.'
    },
    unSubmittedChangesWarningMsg1: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.unSubmittedChangesWarningMsg1',
        defaultMessage: "You can't save changes since you have edits in progress."
    },
    reviewUnSubmittedChanges: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.reviewUnSubmittedChanges',
        defaultMessage: 'Review edits in progress'
    },
    reviewUnSubmittedChanges2: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.reviewUnSubmittedChanges2',
        defaultMessage: 'and submit or discard them to proceed.'
    },
    reviewUnSubmittedChangesAnnouncer: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.reviewUnSubmittedChangesAnnouncer',
        defaultMessage:
            "You can't save changes since you have edits in progress. Review edits in progress and submit or discard them to proceed."
    },
    pairsCountWithoutFiltering: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.pairsCountWithoutFiltering',
        defaultMessage: '{totalCount} pairs'
    },
    pairsCountWithFiltering: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.pairsCountWithFiltering',
        defaultMessage: '{filteredCount} of {totalCount} pairs'
    },
    loadingKnowledgeBaseFailedTitle: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.loadingKnowledgeBaseFailedTitle',
        defaultMessage: 'Failed to load QnA pairs'
    },
    smartSuggestionsMessage1: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.smartSuggestionsMessage1',
        defaultMessage: 'You have new suggestions of alternate questions for your question answer pairs. Go to'
    },
    smartSuggestionsMessageLink: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.smartSuggestionsMessageLink',
        defaultMessage: 'Review suggestions'
    },
    smartSuggestionsMessage2: {
        id: 'qnaMaker.qnaMakerEditQuestionAnswerPairs.smartSuggestionsMessage2',
        defaultMessage: 'to view them'
    }
});

/**
 * Renders the questions answer pairs pivot in the edit page for the QnA Maker service.
 */
export const QnaMakerEditQuestionAnswerPairs = observer(() => {
    const { localize } = useLocalization();

    const history = useHistory();
    const location = useLocation<{ qnaId: string }>();
    const decodedQnaPairId = location.state?.qnaId;
    const { rootStore, dispatcher, uiStore: portalUiStore } = useGlobalState();
    const [pageChangeTrigger, setPageChangeTrigger] = React.useState(getId());
    const uiStore = useLocalStore<QnaMakerQnaPairsUiStore>(() => new QnaMakerQnaPairsUiStore(portalUiStore));
    const [openedPromptModalId, setOpenedPromptModalId] = React.useState<number>(null);

    const searchParams = new URLSearchParams(location.search);
    const decodedSourceFilterName = searchParams.get('source');
    const isAnnouncedWarningMessageActive = React.useRef(true);

    const {
        searchQuery,
        isFilterBladeOpen,
        isAddQnaPairOn,
        savingState,
        deleteModalSettings,
        isRichTextEnabled,
        filtersStores,
        dirtyQnaPairs,
        importProjectModalSettings,
        contextTreeMap,
        isTestPaneOpen,
        qnaPairsJson,
        displayedQnaPairStores,
        isContextTreeCalculating,
        qnaPairsBeingEditedStore,
        isSmartSuggestionsMessageDisplayed
    } = uiStore;

    const {
        qnaMetricStore,
        id: currentProjectId,
        name: currentProjectName,
        qnasJsonData,
        areQnaPairsLoaded,
        haveSmartSuggestions,
        isSmartSuggestionEnabled,
        sourcesStoreHouse: { items: sourceStores }
    } = rootStore.currentQnaMakerProjectStore;

    const { serviceName } = getPortalFromUrl();
    const qnasFetchingTracker = useProgressTracker();
    const { generateIdForNewPair, getNewPairId } = useIdGeneratorForNewQnaPair();
    const isTableLoading = qnasFetchingTracker.isRunning || !areQnaPairsLoaded;

    const tableRef = React.useRef<IDetailsList>();

    const isInEmptyState: boolean = !isTableLoading && !qnaPairsJson.length && !isAddQnaPairOn;

    const filteredQnas = React.useMemo(
        () =>
            substringSearchStrategy(qnaPairsJson, searchQuery, ['questions', 'answer'])
                .filter(
                    pair =>
                        applySourcesFilter(filtersStores.appliedSourcesFilters, pair) &&
                        applyMetadataFilter(filtersStores.appliedMetadataFilters, pair) &&
                        applyQuestionFilter(
                            filtersStores.appliedQuestionFilters,
                            pair,
                            qnaPairsBeingEditedStore.qnaPairsBeingEditedIds,
                            dirtyQnaPairs.map(p => p.value.id)
                        )
                )
                .sort((qna1, qna2) => {
                    if (qna1.source === 'Editorial') {
                        return qna1.id > qna2.id ? -1 : 1;
                    }

                    if (qna1.source !== qna2.source) {
                        return qna1.id > qna2.id ? -1 : 1;
                    }

                    return qna1.id > qna2.id ? 1 : -1;
                }),

        [
            searchQuery,
            qnaPairsJson,
            dirtyQnaPairs,
            pageChangeTrigger,
            filtersStores.appliedSourcesFilters,
            filtersStores.appliedMetadataFilters,
            filtersStores.appliedQuestionFilters,
            qnaPairsBeingEditedStore.qnaPairsBeingEditedIds
        ]
    );

    const { addNewRowInAddMode, resetMetricConfigsAfterSave, setTimestampToStartEditingOrAdding } = useQnaMetricActions();
    const { items, currentPage, pageCount, onPageChange } = useQnaPaginatedAssets({
        pageSize: LuisConstants.QnaMaker.NumberOfDisplayedQnaPairsPerPage,
        assets: filteredQnas
    });

    const { selectedItems, selection } = useSelection<QnaMakerQnaStore>();

    const { editQnaPair, saveChanges, addQnaPair, deleteQnaPair, exportQnas, importQnas, savingTracker } = useQnaPairsActions(uiStore);

    const handlePageChange = (pageNumber: number) => {
        onPageChange(pageNumber);
        setPageChangeTrigger(getId());
        selection.setAllSelected(false);
        tableRef.current?.focusIndex(0);
    };

    const handleOnResetFilters = () => {
        if (decodedSourceFilterName) {
            history.push(routeTable.qnaMaker.editPage(currentProjectId));
        }
        filtersStores.resetFilters();
    };

    const handleRemoveFilter = (filterType: QnaMakerFiltersType) => {
        if (filterType === 'sources' && decodedSourceFilterName) {
            history.push(routeTable.qnaMaker.editPage(currentProjectId));
        }
        filtersStores.resetFilters(filterType);
    };

    const handleSubmitAddQnaPair = (newData: Partial<QnaMakerQnaSwagger>) => {
        selection.setAllSelected(false);
        addQnaPair(newData);
    };

    const handleSubmitEditQnaPair = (id: number, newData: Partial<QnaMakerQnaSwagger>) => {
        if (qnaPairsBeingEditedStore.hasPair(id)) {
            const dataToBeSubmitted = qnaPairsBeingEditedStore.getPair(id);
            editQnaPair(id, dataToBeSubmitted);
            qnaPairsBeingEditedStore.deletePair(id);
        } else {
            editQnaPair(id, newData);
        }
    };

    const handleQnaContextualNavigation = (qnaId: number, delay: number = 0) => {
        if (filtersStores.allAppliedFilters.length || Boolean(searchQuery)) {
            uiStore.setSearchQuery('');
            filtersStores.resetFilters();
        }

        const qnaIndex = filteredQnas.findIndex(qna => qna.id === qnaId);
        const qnaPage = Math.floor(qnaIndex / LuisConstants.QnaMaker.NumberOfDisplayedQnaPairsPerPage);
        const qnaIndexInPage = Math.floor(qnaIndex % LuisConstants.QnaMaker.NumberOfDisplayedQnaPairsPerPage);

        if (qnaPage !== currentPage) {
            handlePageChange(qnaPage);
        }

        setTimeout(() => {
            tableRef.current.focusIndex(qnaIndexInPage, false, noopUndefined, ScrollToMode.top);
            (document.querySelector(`#qna_${qnaId} > div`) as HTMLElement)?.click();

            if (decodedQnaPairId) {
                // Needs to wait until the pair is selected and then reset the location state
                setTimeout(() => {
                    history.replace({ ...location, state: { ...location.state, qnaId: undefined } });
                }, 500);
            }
        }, delay);
    };

    const handleEditQnaPairFromTest = (qnaId: number) => {
        uiStore.setIsTestPaneOpen(false);

        // the setTimeout is needed because whenever we close the test pane
        // the scrolling of the whole page is reset to the top.
        handleQnaContextualNavigation(qnaId, 500);
        qnaPairsBeingEditedStore.addPair(filteredQnas.find(q => q.id === qnaId));
    };

    const deleteModalRenderer = () => {
        let title;
        let body;
        switch (deleteModalSettings.operation) {
            case 'metadata':
                title = localize(messages.deleteQnaMetadataModalTitle);
                body = localize(messages.deleteQnaMetadataModalBody);
                break;
            case 'followUpPrompts':
                title = localize(messages.deleteQnaPromptsModalTitle);
                body = localize(messages.deleteQnaPromptsModalBody);
                break;
            default:
                title = localize(messages.deleteQnaPairModalTitle);
                body = localize(messages.deleteQnaPairModalBody, {
                    selectedCount: selectedItems.length
                });
        }

        return (
            <DeletePromptModal
                dialogContentProps={{ title }}
                hidden={deleteModalSettings.hidden}
                onDelete={() => {
                    deleteQnaPair(
                        deleteModalSettings.operation,
                        selectedItems.map(selectedItem => selectedItem.id)
                    );
                    selection.setAllSelected(false);
                }}
                onDismiss={() => {
                    uiStore.setDeleteModalSettings({ hidden: true, operation: null });
                    selection.setAllSelected(false);
                }}>
                {body}
            </DeletePromptModal>
        );
    };

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerAssets', {
            tracker: qnasFetchingTracker,
            toastData: {
                failed: {
                    title: localize(messages.loadingKnowledgeBaseFailedTitle),
                    content: qnasFetchingTracker.error?.message
                }
            }
        });

        return () => {
            rootStore.currentQnaMakerProjectStore?.resetQnaPairsLoadedState();
        };
    }, []);

    React.useEffect(() => {
        if (savingTracker.isFailed) {
            uiStore.setSavingState('dirty');
        }
    }, [savingTracker.isFailed]);

    React.useEffect(() => {
        if (sourceStores.length && decodedSourceFilterName && !filtersStores.appliedSourcesFilters.length) {
            filtersStores.applySourceFilterById(decodedSourceFilterName);
        }
    }, [sourceStores.length, qnasJsonData]);

    React.useEffect(() => {
        if (qnasJsonData.length && decodedQnaPairId && tableRef.current) {
            handleQnaContextualNavigation(Number(decodedQnaPairId));
        }
    }, [qnasJsonData, tableRef.current]);

    React.useEffect(() => {
        uiStore.setQnaPairsJson(qnasJsonData);
    }, [qnasJsonData]);

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerSources', { tracker: qnasFetchingTracker });
    }, []);

    React.useEffect(() => {
        filtersStores.setMetadataFiltersItems(qnaPairsJson);
        uiStore.setIsSmartSuggestionsMessageDisplayed(isSmartSuggestionEnabled && haveSmartSuggestions);

        if (!sourceStores.find(s => s.source === 'Editorial') && dirtyQnaPairs.some(d => d.op === 'add')) {
            dispatcher.dispatch('getQnaMakerSources', { tracker: qnasFetchingTracker, context: { forceRefresh: true } });
        }
    }, [qnaPairsJson]);

    React.useEffect(() => {
        filtersStores.setSourcesFiltersItems(sourceStores);
    }, [sourceStores]);

    React.useEffect(() => {
        uiStore.setDisplayedQnaPairStores(items as QnaMakerQnaStore[]);
    }, [items]);

    React.useEffect(() => {
        isAnnouncedWarningMessageActive.current = savingState !== 'dirty';
    }, [savingState]);

    const handleSaveChanges = () => {
        logButtonClickWithCustomData(serviceName, 'EditKb', 'SaveChanges', {
            pivotName: 'Qnas',
            projectId: currentProjectId,
            totalTimeToSave: Date.now() - qnaMetricStore.timestampToStartEditingOrAdding,
            totalNumberOfClicks: qnaMetricStore.totalNumberOfClicksBeforeSave + LuisConstants.QnaMaker.Metrics.saveClickCounter
        });

        resetMetricConfigsAfterSave();

        saveChanges(true);

        if (filtersStores.appliedQuestionFilters.find(f => f.id === QuestionsBeingEditedFilterItem)) {
            filtersStores.resetFilters('questions');
        }
    };

    const handleAddQna = () => {
        addNewRowInAddMode(generateIdForNewPair());
        setTimestampToStartEditingOrAdding(Date.now());

        logButtonClick(serviceName, 'EditKb', 'AddQApair', 'Qnas', currentProjectId);
        uiStore.setIsAddQnaPairOn(true);
    };

    const handleEditQna = () => {
        setTimestampToStartEditingOrAdding(Date.now());
        logButtonClick(serviceName, 'EditKb', 'Edit', 'Qnas', currentProjectId);
        selectedItems.map(sI =>
            qnaPairsBeingEditedStore.addPair(qnaMakerQnaStoreToQnaSwagger(displayedQnaPairStores.find(q => q.id === sI.id)))
        );
    };

    const handleTestQna = () => {
        logButtonClick(serviceName, 'EditKb', 'Test', 'Qnas', currentProjectId);
        uiStore.setIsTestPaneOpen(!isTestPaneOpen);
    };

    const handleDeleteQna = (operation: DeletionOperations) => {
        logButtonClick(serviceName, 'EditKb', 'Delete', 'Qnas', currentProjectId);
        uiStore.setDeleteModalSettings({ hidden: false, operation });
    };

    const handleRichText = () => {
        logToggleChange(serviceName, 'EditKb', 'EnableRichText', isRichTextEnabled ? 'Off' : 'On', 'Qnas', currentProjectId);
        uiStore.toggleIsRichTextEnabled();
    };

    const handleImport = (fileType: 'tsv' | 'xlsx') => {
        logButtonClick(serviceName, 'EditKb', fileType === 'xlsx' ? 'ImportExcel' : 'ImportTsv', 'Qnas', currentProjectId);
        uiStore.setImportProjectModalSettings({ hidden: false, fileType });
    };

    return (
        <>
            <UnsavedChangesPrompt
                isPageDirty={savingState === 'dirty'}
                promptPath={`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=qnas`}
            />

            <UnSubmittedChangesPrompt
                isPageDirty={qnaPairsBeingEditedStore.hasPairsInEditMode}
                promptPath={`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=qnas`}
                onStayCb={() => filtersStores.applyQuestionsFilterById(QuestionsBeingEditedFilterItem)}
            />

            <Stack tokens={{ childrenGap: 10 }} verticalFill>
                <QnaMakerQnaCommandBar
                    onExport={exportQnas}
                    searchQuery={searchQuery}
                    isLoading={isTableLoading}
                    selectedItems={selectedItems}
                    qnasCountText={
                        filtersStores.allAppliedFilters.length || searchQuery
                            ? localize(messages.pairsCountWithFiltering, {
                                  filteredCount: filteredQnas.length,
                                  totalCount: qnaPairsJson.length
                              })
                            : localize(messages.pairsCountWithoutFiltering, { totalCount: qnaPairsJson.length })
                    }
                    isAddQnaPairOn={isAddQnaPairOn}
                    onSave={handleSaveChanges}
                    enableRichText={isRichTextEnabled}
                    isFilterSelected={isFilterBladeOpen}
                    isInDirtyState={savingState !== 'saved'}
                    isPromptModalHidden={!openedPromptModalId}
                    optionalColumnsShown={portalUiStore.qnaMakerEditKbOptionalColumnsVisibility}
                    onFilter={() => uiStore.toggleIsFilterBladeOpen()}
                    isInEmptyState={uiStore.qnaPairsJson.length === 0}
                    onAddNewQna={handleAddQna}
                    onChangeSearchQuery={q => uiStore.setSearchQuery(q)}
                    onTest={handleTestQna}
                    areEditActionsDisabled={uiStore.savingState === 'inProgress'}
                    isEditQnaPairOn={qnaPairsBeingEditedStore.hasPairsInEditMode}
                    onChangeEnableRichText={handleRichText}
                    onChangeOptionalColumnsShown={key => dispatcher.dispatch('toggleQnaMakerEditKbOptionalColumnsVisibility', { key })}
                    onImport={handleImport}
                    onDelete={handleDeleteQna}
                    onEdit={handleEditQna}
                />

                {qnaPairsBeingEditedStore.hasPairsInEditMode || isAddQnaPairOn ? (
                    <>
                        <MessageBar
                            isMultiline
                            dismissButtonAriaLabel="Close"
                            styles={{ root: { width: '100%' } }}
                            messageBarType={MessageBarType.warning}>
                            {localize(messages.unSubmittedChangesWarningMsg1)}
                            &nbsp;
                            {!filtersStores.appliedQuestionFilters.find(f => f.id === QuestionsBeingEditedFilterItem) && (
                                <>
                                    <Link
                                        as="button"
                                        onClick={() => filtersStores.applyQuestionsFilterById(QuestionsBeingEditedFilterItem)}>
                                        {localize(messages.reviewUnSubmittedChanges)}
                                    </Link>
                                    &nbsp;
                                    {localize(messages.reviewUnSubmittedChanges2)}
                                </>
                            )}
                        </MessageBar>
                        <Announced aria-live="assertive" message={localize(messages.reviewUnSubmittedChangesAnnouncer)} />
                    </>
                ) : (
                    savingState === 'dirty' && (
                        <>
                            <MessageBar
                                isMultiline={false}
                                dismissButtonAriaLabel="Close"
                                styles={{ root: { width: '100%' } }}
                                messageBarType={MessageBarType.warning}>
                                {localize(messages.unsavedChangesWarningMsg)}
                            </MessageBar>
                            <Announced aria-live="assertive" message={localize(messages.unsavedChangesWarningMsg)} />
                        </>
                    )
                )}

                {isSmartSuggestionsMessageDisplayed && (
                    <>
                        <MessageBar
                            isMultiline={false}
                            dismissButtonAriaLabel="Close"
                            styles={{ root: { width: '100%' } }}
                            onDismiss={() => uiStore.setIsSmartSuggestionsMessageDisplayed(false)}
                            messageBarType={MessageBarType.info}>
                            {localize(messages.smartSuggestionsMessage1)}
                            &nbsp;
                            <Link as="button" onClick={() => history.push(routeTable.qnaMaker.reviewPage(currentProjectId))}>
                                {localize(messages.smartSuggestionsMessageLink)}
                            </Link>
                            &nbsp;
                            {localize(messages.smartSuggestionsMessage2)}
                        </MessageBar>
                        <Announced aria-live="assertive" message={localize(messages.unsavedChangesWarningMsg)} />
                    </>
                )}

                {filtersStores.allAppliedFilters.length && (
                    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
                        <LuisText aria-label={localize(messages.filtersApplied)}>{localize(messages.filtersApplied)}</LuisText>

                        <QnaMakerFilterPillContainer filterItems={filtersStores.allAppliedFilters} onRemoveFilter={handleRemoveFilter} />
                        <Link as="button" disabled={uiStore.savingState === 'inProgress'} onClick={handleOnResetFilters}>
                            <span aria-label={localize(messages.clearAllAriaLabel)}>{localize(messages.clearAll)}</span>
                        </Link>
                    </Stack>
                )}

                {isInEmptyState ? (
                    <EmptyStateView message={<LuisText>{localize(messages.emptyStateMessage)}</LuisText>} />
                ) : (
                    <>
                        <QnaMakerQnaTable
                            selection={selection}
                            tableRef={tableRef}
                            isLoading={isTableLoading}
                            sourceStores={sourceStores}
                            qnaPairsJson={qnaPairsJson}
                            getNewPairId={getNewPairId}
                            items={displayedQnaPairStores}
                            onSubmitAddQnaPair={handleSubmitAddQnaPair}
                            contextTreeMap={contextTreeMap}
                            isAddQnaPairOn={isAddQnaPairOn}
                            dirtyQnaPairs={dirtyQnaPairs}
                            isRichTextEnabled={isRichTextEnabled}
                            overallItemsCount={filteredQnas.length}
                            openedPromptModalId={openedPromptModalId}
                            onSubmitEditQnaPair={handleSubmitEditQnaPair}
                            emptyPlaceholder={localize(messages.noItemsFound)}
                            isContextTreeCalculating={isContextTreeCalculating}
                            qnaPairsBeingEditedStore={qnaPairsBeingEditedStore}
                            optionalColumnsVisibility={portalUiStore.qnaMakerEditKbOptionalColumnsVisibility}
                            firstQnaWithAlternateQsId={
                                !portalUiStore.teachingBubbles.QnaAlternateQuestions && uiStore.firstQnaWithAlternateQsId
                            }
                            onDismissAddQnaPair={() => uiStore.setIsAddQnaPairOn(false)}
                            handleQnaContextualNavigation={handleQnaContextualNavigation}
                            areQnaTableActionsDisabled={uiStore.savingState === 'inProgress'}
                            onDismissEditQnaPair={id => qnaPairsBeingEditedStore.deletePair(id)}
                            onOpenedPromptModalChange={setOpenedPromptModalId}
                            areItemsFiltered={Boolean(searchQuery || filtersStores.allAppliedFilters.length)}
                            setDetailsQnaPairId={(id: number) => uiStore.setDetailsQnaPairId(id)}
                        />
                        <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => handlePageChange(p.selected)} />
                    </>
                )}

                {deleteModalRenderer()}

                <ImportModal
                    isNameFieldDisplayed={false}
                    hidden={importProjectModalSettings.hidden}
                    fileType={importProjectModalSettings.fileType}
                    dialogContentProps={{ title: localize(messages.importModalTitle) }}
                    onDismiss={() => uiStore.setImportProjectModalSettings({ hidden: true })}
                    nameValidators={[FormValidators.createBlacklistValidator({ characterList: `` })]}
                    onSubmit={data => {
                        importQnas({ ...data, fileType: importProjectModalSettings.fileType });
                        uiStore.setImportProjectModalSettings({ hidden: true });
                    }}
                />

                <QnaMakerTestPane
                    isOpen={uiStore.isTestPaneOpen}
                    onEditQnA={handleEditQnaPairFromTest}
                    onDismiss={() => {
                        uiStore.setIsTestPaneOpen(false);
                        selection.setAllSelected(false);
                    }}
                    onAddQuestion={(answerId, questions) => {
                        editQnaPair(answerId, { questions });
                        selection.setAllSelected(false);
                    }}
                    flattenMetadata={getFlattenMetadata(qnaPairsJson)}
                />

                {isFilterBladeOpen && (
                    <QnaMakerFiltersBlade
                        isOpen={isFilterBladeOpen}
                        sourcesFilters={filtersStores.sourcesFilters}
                        metadataFilters={filtersStores.metadataFilters}
                        questionsFilters={filtersStores.questionsFilters}
                        suggestionFilters={filtersStores.suggestionFilters}
                        onDismiss={() => uiStore.toggleIsFilterBladeOpen()}
                        onFiltersChange={filters => {
                            filtersStores.setFilterItems(filters);
                            selection.setAllSelected(false);
                        }}
                    />
                )}
            </Stack>
        </>
    );
});
